import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Checkbox, Typography, TextField, Select, MenuItem, FormControl, FormControlLabel, Box, Button, FormHelperText, Snackbar, ListItemText, OutlinedInput } from '@mui/material';
import { useState, useEffect } from 'react';
import { Icon } from "@iconify/react";
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import '../subscriptionPlans.css'
import { subscriptionCreateOrUpdate, subscriptionMetaData, subscriptionGetById } from '../subscriptionPlans.service';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import Loader from '../../../../components/loader';
import RichTextEditor from '../../../../components/textEditor';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

function SubcriptionsCreation() {

    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get("type");
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [alert, setAlert] = useState('');
    const [responseMsg, setResponseMsg] = useState("");
    const [userTypeData, setUserTypeData] = useState([]);
    const [tenureData, setTenureData] = useState([]);
    const ALERT_DURATION = 2000;
    const alertClose = () => {
        setAlert('')
    }

    useEffect(() => {
        if (type === 'edit') {
            setEditMode(true);
        }
        fetchData()
        subscriptionPlanMetaData()
    }, [type, editMode]);

    const subscriptionSchema = z.object({
        planName: z.string().min(1, { message: "Plan Name is required" }),
        tenure: z.string().min(1, { message: "Tenure is required" }).or(z.number()),
        userTypeIds: z.array(z.number()).min(1, { message: "Select at least one user type" }),
        description: z.string()
            .transform((val) => val.replace(/<[^>]*>/g, '').trim())
            .refine((val) => val !== '', {
                message: 'Description is required',
            }),
        setupFee: z.string().min(1, { message: "Setup Fee is required" }),
        subscriptionFee: z.string().min(1, { message: "Subscription Fee is required" }),
        promotionCode: z.string().min(1, { message: "Promo Code is required" }),
        promotionDiscount: z.string().min(1, { message: "Discount Percentage is required" }),
    });

    const { handleSubmit, formState: { errors }, control, reset, setValue, getValues } = useForm({
        resolver: zodResolver(subscriptionSchema), defaultValues: {
            description: '',
        },
    });

    const fetchData = async () => {
        if (editMode === true) {
            const getItem = subscriptionGetById(id).then(async (res) => {
                let subscriptionData = res.data
                setValue('planName', subscriptionData?.planName ? subscriptionData.planName : '');
                setValue('tenure', subscriptionData?.tenure ? subscriptionData.tenure : '');
                setValue('userTypeIds', subscriptionData?.userTypes?.map((u) => u.id) || []);
                setValue('description', subscriptionData?.description ? subscriptionData.description : '');
                setValue('setupFee', subscriptionData?.setupFee ? subscriptionData.setupFee : '');
                setValue('subscriptionFee', subscriptionData?.subscriptionFee ? subscriptionData.subscriptionFee : '');
                setValue('promotionCode', subscriptionData?.promotionCode ? subscriptionData.promotionCode : '');
                setValue('promotionDiscount', subscriptionData?.promotionDiscount ? subscriptionData.promotionDiscount : '');
            })
        }
    };

    const onSubmit = async (data) => {
        try {
            setLoading(true)
            const addAnotherPlan = getValues("anotherPlan")
            subscriptionCreateOrUpdate(data, editMode, id).then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201) {
                    setResponseMsg(res.message);
                    setAlert('true');
                    setOpenAlert(true)
                    setLoading(false)
                    setTimeout(() => {
                        alertClose();
                        reset()
                    }, ALERT_DURATION);
                    if (!addAnotherPlan) {
                        setTimeout(() => {
                            editMode
                                ? navigate(`/subscriptionView/${id}?type=edit`)
                                : navigate('/subscriptionPlans');
                            alertClose();
                        }, ALERT_DURATION);
                    }
                } else if (res.statusCode === 404) {
                    setResponseMsg(res.message);
                    setAlert('false');
                    setOpenAlert(true)
                    setLoading(false)
                    setTimeout(() => {
                        alertClose();
                    }, ALERT_DURATION);
                } else if (res.statusCode === 500 || res.statusCode === 400 || res.statusCode === 429) {
                    setResponseMsg(res.message);
                    setAlert('false');
                    setOpenAlert(true)
                    setLoading(false)
                    setTimeout(() => {
                        alertClose();
                    }, ALERT_DURATION);
                }
                else {
                    setResponseMsg(res.message);
                    setAlert('false');
                    setLoading(false)
                    setOpenAlert(true)
                    setTimeout(() => {
                        alertClose()
                    }, ALERT_DURATION);
                }
            })
        } catch (error) {
            console.error('User type creation failed:', error);
        }
    };

    const subscriptionPlanMetaData = async () => {
        subscriptionMetaData().then((res) => {
            if (res.statusCode === 200) {
                setUserTypeData(res.data.userTypes);
                setTenureData(res.data.tenures)
            }
        })
    }

    return (
        <Box>
            <Grid container alignItems={'center'} mb={2}>
                <Grid size={{ xs: 11, sm: 11, md: 11, lg: 11, xl: 11 }}>
                    <Typography className='h1'> {editMode ? "Update Subcription" : 'Create Subcription'} </Typography>
                </Grid>
                <Grid size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }} textAlign={'end'}>
                    <Icon icon="ei:arrow-left"
                        onClick={() => {
                            editMode
                                ? navigate(`/subscriptionView/${id}?type=edit`)
                                : navigate('/subscriptionPlans');
                        }} width="30" height="30" style={{ color: '#285D54', cursor: 'pointer' }} />
                </Grid>
            </Grid>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container mt={2} columnGap={6}>
                    <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
                        <Typography style={{fontWeight:'700'}}>Plan Details</Typography>
                        <Grid container mt={2} columnGap={4}>
                            <Grid size={12}>
                                <Grid container columnGap={2} alignItems={'center'}>
                                    <Typography mb={1} data-testid="plane-name" style={{fontWeight:'700'}}  >Plan Name <span style={{ color: 'var(--red-color)' }}>*</span></Typography>
                                    <Grid size={12}>
                                        <FormControl fullWidth>
                                            <Controller name="planName" control={control} defaultValue=""
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField id="outlined-basic" placeholder='Enter plan name' variant="outlined" {...field} error={!!error} helperText={error ? error.message : ''} />
                                                )} />
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid container mt={2} data-testid="tenure-section">
                                    <Typography mb={1} style={{fontWeight:'700'}}>Select Tenure <span style={{ color: 'var(--red-color)' }}>*</span></Typography>
                                    <Grid size={12}>
                                        <FormControl fullWidth error={!!errors.tenure}>
                                            <Controller name="tenure" control={control} defaultValue=""
                                                render={({ field }) => (
                                                    <Select {...field} displayEmpty onChange={(e) => field.onChange(e.target.value)}>
                                                        <MenuItem value="" disabled>
                                                            Tenure Type e.g. Monthly / Yearly
                                                        </MenuItem>
                                                        {Object.entries(tenureData).map(([code, name]) => (
                                                            <MenuItem key={code} value={code}>
                                                                {name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )} />
                                            {tenureData.length === 0 && <MenuItem>No Options</MenuItem>}

                                            {!!errors.tenure && (
                                                <FormHelperText>{errors.tenure.message}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid containermt={2} mt={2} >
                                    <Grid container>
                                        <Grid size={{ xs: 0.5, sm: 1, md: 1, lg: 0.5, xl: 0.5 }}>
                                            <span style={{ marginTop: '4px' }}> <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" >
                                                <path d="M7.6335 9.05832C7.55017 9.04999 7.45017 9.04999 7.3585 9.05832C5.37517 8.99166 3.80017 7.36666 3.80017 5.36666C3.80017 3.32499 5.45017 1.66666 7.50017 1.66666C9.54187 1.66666 11.2002 3.32499 11.2002 5.36666C11.1919 7.36666 9.61687 8.99166 7.6335 9.05832Z" stroke="#1C1B1F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M13.6751 3.33334C15.2918 3.33334 16.5918 4.64168 16.5918 6.25001C16.5918 7.82501 15.3418 9.10834 13.7834 9.16668C13.7168 9.15834 13.6418 9.15834 13.5668 9.16668" stroke="#1C1B1F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M3.4666 12.1333C1.44993 13.4833 1.44993 15.6833 3.4666 17.025C5.75827 18.5583 9.51663 18.5583 11.8083 17.025C13.825 15.675 13.825 13.475 11.8083 12.1333C9.52496 10.6083 5.7666 10.6083 3.4666 12.1333Z" stroke="#1C1B1F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M15.2836 16.6667C15.8836 16.5417 16.4502 16.3 16.9169 15.9417C18.2169 14.9667 18.2169 13.3583 16.9169 12.3833C16.4586 12.0333 15.9002 11.8 15.3086 11.6667" stroke="#1C1B1F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg></span>
                                        </Grid>
                                        <Grid size={{ xs: 11.5, sm: 11, md: 11, lg: 11.5, xl: 11.5 }}>
                                            <Typography mb={1} data-testid="user-types-section" style={{fontWeight:'700'}}>   User Types  <span style={{ color: 'var(--red-color)' }}>*</span></Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12}>
                                        <FormControl fullWidth error={!!errors.userTypeIds}>
                                            <Controller name="userTypeIds" control={control} defaultValue={[]}
                                                render={({ field }) => (
                                                    <Select labelId="user-type-label" id="user-type-select" multiple  {...field} value={field.value || []}
                                                        onChange={(event) => {
                                                            field.onChange(event.target.value);
                                                        }}
                                                        input={<OutlinedInput />}
                                                        renderValue={(selected) =>
                                                            selected
                                                                .map((id) => {
                                                                    const user = userTypeData.find((item) => item.id === id);
                                                                    return user?.typeName || id;
                                                                })
                                                                .join(', ')
                                                        } MenuProps={MenuProps}>
                                                        {userTypeData.map((item) => (
                                                            <MenuItem key={item.id} value={item.id}>
                                                                <Checkbox checked={field.value.includes(item.id)} sx={{ padding: 0.3 }} />
                                                                <ListItemText>
                                                                    <Typography sx={{ fontSize: 12 }}>{item.typeName}</Typography>
                                                                </ListItemText>
                                                            </MenuItem>
                                                        ))}
                                                        {userTypeData.length === 0 && <MenuItem>No Options</MenuItem>}
                                                    </Select>
                                                )} />
                                            {!!errors.userTypeIds && <FormHelperText>{errors.userTypeIds.message}</FormHelperText>}
                                            <Typography className='labelDescription' mt={1}> Select one or more user types </Typography>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid container mt={2}>
                                    <Typography mb={1} style={{fontWeight:'700'}}>
                                        Description <span style={{ color: 'var(--red-color)' }}>*</span>
                                    </Typography>
                                    <Grid size={12}>
                                        <FormControl fullWidth>
                                            <Controller name="description" control={control} rules={{ required: 'Description is required' }}
                                                render={({ field, fieldState }) => (
                                                    <RichTextEditor value={field.value} onChange={field.onChange} error={fieldState.error} />
                                                )}
                                            />
                                            {errors.description && (
                                                <FormHelperText error>{errors.description.message}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }} mt={1}>

                        <Typography className='heading' style={{fontWeight:'700'}}>Fee Details</Typography>

                        <Typography mb={1} mt={1} style={{fontWeight:'700'}}> Setup Fee in $ <span style={{ color: 'var(--red-color)' }}>*</span></Typography>
                        <Grid container columnGap={2} alignItems={'center'}>
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <Controller
                                        name="setupFee"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                            validate: (value) =>
                                                value === '' || parseFloat(value) >= 0 || 'Only positive values are allowed',
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                type="number"
                                                {...field}
                                                id="outlined-basic"
                                                placeholder="0"
                                                variant="outlined"
                                                inputProps={{ min: 0, step: 0.01 }}
                                                error={!!error}
                                                helperText={error ? error.message : ''}
                                            />
                                        )}
                                    />
                                </FormControl>

                            </Grid>
                        </Grid>

                        <Grid container mt={2} >
                            <Typography mb={1}style={{fontWeight:'700'}} >Subscription Fee in $ <span style={{ color: 'var(--red-color)' }}>*</span></Typography>
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <Controller name="subscriptionFee" control={control} defaultValue="" rules={{
                                        validate: (value) =>
                                            value === '' || parseFloat(value) >= 0 || 'Only positive values are allowed',
                                    }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField type='number' {...field} id="outlined-basic" placeholder='0' variant="outlined"   {...field} error={!!error} helperText={error ? error.message : ''}   inputProps={{ min: 0, step: 0.01 }} />
                                        )} />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Typography mt={3} className='heading' data-testid="promotional-discount">Promotional Discount  </Typography>
                        <Grid container mt={2}>
                            <Grid size={{ xs: 0.5, sm: 1, md: 1, lg: 0.5, xl: 0.5 }}>
                                <span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M18.75 3.63333V16.3667H1.25V12.3833C1.8821 12.3833 2.48831 12.1322 2.93527 11.6853C3.38223 11.2383 3.63333 10.6321 3.63333 10C3.63333 9.3679 3.38223 8.76169 2.93527 8.31473C2.48831 7.86777 1.8821 7.61667 1.25 7.61666V3.63333H18.75Z" stroke="#1C1B1F" stroke-width="1.59167" stroke-miterlimit="10" stroke-linecap="square" />
                                        <path d="M6.81677 6.81665H8.40844" stroke="#1C1B1F" stroke-width="1.59167" stroke-miterlimit="10" />
                                        <path d="M6.81677 9.99997H8.40844" stroke="#1C1B1F" stroke-width="1.59167" stroke-miterlimit="10" />
                                        <path d="M6.81677 13.1833H8.40844" stroke="#1C1B1F" stroke-width="1.59167" stroke-miterlimit="10" />
                                    </svg>
                                </span>
                            </Grid>
                            <Grid size={{ xs: 11.5, sm: 11, md: 11, lg: 11.5, xl: 11.5 }}>
                                <Typography mb={1} data-testid="user-types-section" style={{fontWeight:'700'}}> Promo Code  <span style={{ color: 'var(--red-color)' }}>*</span></Typography>
                            </Grid>
                        </Grid>
                        <Grid container mt={1} >
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <Controller name="promotionCode" control={control} defaultValue=""
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField  {...field} id="outlined-basic" placeholder='e.g. SUMMER 2.0' variant="outlined"   {...field} error={!!error} helperText={error ? error.message : ''} inputProps={{ 'data-testid': 'promotion-code' }} />
                                        )} />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container mt={3}>
                            <Grid size={{ xs: 0.5, sm: 1, md: 1, lg: 0.5, xl: 0.5 }}>
                                <span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M8.3333 7.49997C8.79355 7.49997 9.16663 7.87306 9.16663 8.3333C9.16663 8.79357 8.79355 9.16665 8.3333 9.16665C7.87309 9.16665 7.5 8.79357 7.5 8.3333C7.5 7.87306 7.87309 7.49997 8.3333 7.49997Z" fill="#1C1B1F" />
                                        <path d="M10.8334 11.6667C10.8334 12.1269 11.2065 12.5 11.6667 12.5C12.127 12.5 12.5 12.1269 12.5 11.6667C12.5 11.2064 12.127 10.8333 11.6667 10.8333C11.2065 10.8333 10.8334 11.2064 10.8334 11.6667Z" fill="#1C1B1F" />
                                        <path d="M8.92255 12.2559L12.2559 8.92257C12.5814 8.59715 12.5814 8.06949 12.2559 7.74404C11.9305 7.41861 11.4029 7.41861 11.0774 7.74404L7.74408 11.0774C7.41864 11.4028 7.41864 11.9305 7.74408 12.2559C8.06952 12.5813 8.59713 12.5813 8.92255 12.2559Z" fill="#1C1B1F" />
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.593 3.3929L12.6422 3.62088L12.1311 2.7874C11.1556 1.19648 8.84413 1.19648 7.86866 2.7874L7.35759 3.62088L6.40685 3.3929C4.59212 2.95773 2.95764 4.59221 3.3928 6.40694L3.62079 7.35769L2.7873 7.86875C1.19639 8.84427 1.19639 11.1558 2.7873 12.1313L3.62079 12.6424L3.3928 13.5931C2.95764 15.4078 4.59212 17.0423 6.40685 16.6071L7.35759 16.3791L7.86866 17.2126C8.84413 18.8035 11.1556 18.8035 12.1311 17.2126L12.6422 16.3791L13.593 16.6071C15.4077 17.0423 17.0422 15.4078 16.607 13.5931L16.379 12.6424L17.2125 12.1313C18.8035 11.1558 18.8035 8.84427 17.2125 7.86875L16.379 7.35769L16.607 6.40694C17.0422 4.59221 15.4077 2.95773 13.593 3.3929ZM9.28946 3.6586C9.61463 3.1283 10.3851 3.1283 10.7103 3.6586L11.5377 5.00795C11.7263 5.31558 12.0915 5.46685 12.4425 5.3827L13.9816 5.01361C14.5865 4.86856 15.1314 5.41339 14.9863 6.0183L14.6172 7.55747C14.533 7.90838 14.6843 8.27358 14.992 8.46219L16.3413 9.2896C16.8716 9.61477 16.8716 10.3853 16.3413 10.7104L14.992 11.5378C14.6843 11.7264 14.533 12.0916 14.6172 12.4425L14.9863 13.9817C15.1314 14.5866 14.5865 15.1314 13.9816 14.9864L12.4425 14.6173C12.0915 14.5332 11.7263 14.6844 11.5377 14.992L10.7103 16.3414C10.3851 16.8717 9.61463 16.8717 9.28946 16.3414L8.46213 14.992C8.27349 14.6844 7.90829 14.5332 7.55738 14.6173L6.0182 14.9864C5.4133 15.1314 4.86847 14.5866 5.01352 13.9817L5.38261 12.4425C5.46676 12.0916 5.31549 11.7264 5.00785 11.5378L3.65851 10.7104C3.1282 10.3853 3.1282 9.61477 3.65851 9.2896L5.00785 8.46219C5.31549 8.27358 5.46676 7.90838 5.38261 7.55747L5.01352 6.0183C4.86847 5.41339 5.4133 4.86856 6.0182 5.01361L7.55738 5.3827C7.90829 5.46685 8.27349 5.31558 8.46213 5.00795L9.28946 3.6586Z" fill="#1C1B1F" />
                                    </svg>
                                </span>
                            </Grid>
                            <Grid size={{ xs: 11.5, sm: 11, md: 11, lg: 11.5, xl: 11.5 }}>
                                <Typography mb={1} data-testid="user-types-section" style={{fontWeight:'700'}}> Discount Percentage  <span style={{ color: 'var(--red-color)' }}>*</span></Typography>
                            </Grid>
                        </Grid>
                        <Grid container mt={1} >
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <Controller name="promotionDiscount" control={control} defaultValue=""
                                        rules={{
                                            validate: (value) =>
                                                value === '' || parseFloat(value) >= 0 || 'Only positive values are allowed',
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField  {...field} id="outlined-basic" type='number' placeholder="0" variant="outlined" error={!!error} helperText={error ? error.message : ''}
                                            inputProps={{ min: 0, step: 0.01 }}
                                            />
                                        )}
                                    />
                                </FormControl>

                            </Grid>
                            <Typography className='labelDescription'>Enter discount percentage (0-100)</Typography>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid mt={0} style={{ display: editMode ? 'none' : 'block' }}  >
                    <FormControl fullWidth>
                        <Controller name="anotherPlan" control={control} defaultValue={false} render={({ field, fieldState: { error } }) => (
                            <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Add Another Plan" />)} />

                    </FormControl>
                </Grid>

                <Grid container mb={2} mt={1}>
                    <Grid size={10}>
                        <Button variant="outlined" className='resetbtn' onClick={() => { navigate('/subscriptionPlans') }} style={{ display: editMode ? 'none' : 'block' }}  >Cancel</Button>
                    </Grid>
                    <Grid size={2} textAlign={'end'}>
                        <Button variant="contained" className='createBtn' type="submit">{editMode ? "Update Subscription" : 'Create Subscription'}</Button>
                    </Grid>
                </Grid>
            </form>

            {loading && <Loader />}

            <Grid item xs={4} textAlign={'end'}>
                {(alert === 'true') && (
                    <Snackbar open={openAlert} autoHideDuration={6000} onClose={alertClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: '#B6FFC3',
                                py: 1.5,
                                px: 4.5,
                                borderRadius: '30px',
                                boxShadow: 3,
                                maxWidth: 400,
                                mr: -6
                            }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M10 20C9 20 8.25 19.1667 7.66667 18.5C7.33333 18.1667 7 17.75 6.83333 17.6667C6.58333 17.5833 6 17.5833 5.58333 17.5833C4.66667 17.6667 3.66667 17.6667 3 17C2.33333 16.3333 2.33333 15.25 2.41667 14.4167C2.41667 13.9167 2.5 13.4167 2.33333 13.1667C2.25 12.9167 1.83333 12.5833 1.5 12.3333C0.833333 11.75 0 11 0 10C0 9 0.833333 8.25 1.5 7.66667C1.83333 7.33333 2.25 7 2.33333 6.83333C2.41667 6.58333 2.41667 6 2.41667 5.58333C2.33333 4.66667 2.33333 3.66667 3 3C3.66667 2.33333 4.75 2.33333 5.58333 2.41667C6.08333 2.41667 6.58333 2.5 6.83333 2.33333C7.08333 2.25 7.41667 1.83333 7.66667 1.5C8.25 0.833333 9 0 10 0C11 0 11.75 0.833333 12.3333 1.5C12.6667 1.83333 13 2.25 13.1667 2.33333C13.4167 2.41667 14 2.41667 14.4167 2.41667C15.3333 2.33333 16.3333 2.33333 17 3C17.6667 3.66667 17.6667 4.75 17.5833 5.58333C17.5833 6.08333 17.5 6.58333 17.6667 6.83333C17.75 7.08333 18.1667 7.41667 18.5 7.66667C19.1667 8.25 20 9 20 10C20 11 19.1667 11.75 18.5 12.3333C18.1667 12.6667 17.75 13 17.6667 13.1667C17.5833 13.4167 17.5833 14 17.5833 14.4167C17.6667 15.3333 17.6667 16.3333 17 17C16.3333 17.6667 15.25 17.6667 14.4167 17.5833C13.9167 17.5833 13.4167 17.5 13.1667 17.6667C12.9167 17.75 12.5833 18.1667 12.3333 18.5C11.75 19.1667 11 20 10 20ZM6.25 15.9167C6.66667 15.9167 7.08333 16 7.5 16.0833C8.08333 16.3333 8.58333 16.8333 9 17.3333C9.33333 17.75 9.83333 18.25 10.0833 18.25C10.3333 18.25 10.8333 17.75 11.1667 17.3333C11.5833 16.8333 12.0833 16.3333 12.6667 16.0833C13.25 15.8333 14 15.8333 14.6667 15.9167C15.1667 15.9167 15.8333 16 16 15.8333C16.1667 15.6667 16.0833 15 16.0833 14.5C16 13.8333 16 13.0833 16.25 12.5C16.5 11.9167 17 11.4167 17.5 11C17.8333 10.75 18.3333 10.25 18.3333 10C18.3333 9.75 17.8333 9.25 17.4167 8.91667C16.9167 8.5 16.4167 8 16.1667 7.41667C15.9167 6.83333 15.9167 6.08333 16 5.41667C16 4.91667 16.0833 4.25 15.9167 4.08333C15.75 3.91667 15.0833 4 14.5833 4C13.9167 4.08333 13.1667 4.08333 12.5833 3.83333C12 3.58333 11.5 3.08333 11.0833 2.58333C10.75 2.16667 10.25 1.66667 10 1.66667C9.75 1.66667 9.25 2.16667 8.91667 2.58333C8.5 3.08333 8 3.58333 7.41667 3.83333C6.83333 4.08333 6.08333 4.08333 5.41667 4C4.91667 4 4.25 3.91667 4.08333 4.08333C3.91667 4.25 4 4.91667 4 5.41667C4.08333 6.08333 4.08333 6.83333 3.83333 7.41667C3.58333 8 3.08333 8.5 2.58333 8.91667C2.16667 9.25 1.66667 9.75 1.66667 10C1.66667 10.25 2.16667 10.75 2.58333 11.0833C3.08333 11.5 3.58333 12 3.83333 12.5833C4.08333 13.1667 4.08333 13.9167 4 14.5833C4 15.0833 3.91667 15.75 4.08333 15.9167C4.25 16.0833 4.91667 16 5.41667 16C5.66667 16 5.91667 15.9167 6.25 15.9167ZM9.16667 13.6667L6.08333 10.5833L7.25 9.41667L9.16667 11.3333L13.5833 6.91667L14.75 8.08333L9.16667 13.6667Z" fill="#00AD5E" />
                            </svg> &nbsp;&nbsp;
                            <Typography
                                sx={{ color: '#004d40', fontWeight: 600, fontSize: '0.95rem', flexGrow: 1, }}>
                                {responseMsg}
                            </Typography>
                        </Box>
                    </Snackbar>
                )}

                {(alert === 'false') && (
                    <Snackbar open={openAlert} autoHideDuration={6000} onClose={alertClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: '#FDEDED',
                                py: 1.5,
                                px: 4.5,
                                borderRadius: '30px',
                                boxShadow: 3,
                                maxWidth: 400,
                                mr: -6
                            }}
                        >
                            <Icon icon="solar:close-circle-linear" width="26" height="26" style={{ color: 'var(--red-color)' }} /> &nbsp;&nbsp;
                            <Typography
                                sx={{
                                    color: 'var(--red-color)',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    flexGrow: 1,
                                }}>
                                {responseMsg}
                            </Typography>
                        </Box>
                    </Snackbar>
                )}
            </Grid>

        </Box>
    )
}

export default SubcriptionsCreation
